import { useState, useEffect } from 'react';

interface DiagnosticItem {
  name: string;
  icon: string;
  status: 'ok' | 'error' | 'checking';
  detail: string;
}

const initialDiagnostics: DiagnosticItem[] = [
  { name: 'Servidor', icon: 'ri-server-line', status: 'checking', detail: 'Verificando conectividade...' },
  { name: 'Docker', icon: 'ri-ship-2-line', status: 'checking', detail: 'Verificando serviços...' },
  { name: 'Evolution API', icon: 'ri-whatsapp-line', status: 'checking', detail: 'Verificando conexão...' },
  { name: 'PostgreSQL', icon: 'ri-database-2-line', status: 'checking', detail: 'Verificando banco de dados...' },
  { name: 'Redis', icon: 'ri-database-line', status: 'checking', detail: 'Verificando cache...' },
  { name: 'SSL', icon: 'ri-shield-check-line', status: 'checking', detail: 'Verificando certificado...' },
  { name: 'Cloudflare', icon: 'ri-cloud-fill', status: 'checking', detail: 'Verificando DNS/CDN...' },
  { name: 'DNS', icon: 'ri-globe-line', status: 'checking', detail: 'Verificando registros DNS...' },
  { name: 'Nginx', icon: 'ri-globe-line', status: 'checking', detail: 'Verificando proxy reverso...' },
  { name: 'Firewall', icon: 'ri-shield-line', status: 'checking', detail: 'Verificando regras UFW...' },
];

export default function DiagnosticPage() {
  const [items, setItems] = useState<DiagnosticItem[]>(initialDiagnostics);
  const [running, setRunning] = useState(true);
  const [allOk, setAllOk] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => {
    const runDiagnostics = async () => {
      setRunning(true);

      // Simulate checking each service one by one
      const results: Array<{ name: string; status: 'ok' | 'error'; detail: string }> = [
        { name: 'Servidor', status: 'ok', detail: 'Ubuntu 24.04 LTS · Uptime 47 dias · Kernel 6.8.0' },
        { name: 'Docker', status: 'ok', detail: 'v26.1.3 · 12 containers em execução' },
        { name: 'Evolution API', status: 'ok', detail: 'v2.2.1 · 3 instâncias ativas · Conexão estável' },
        { name: 'PostgreSQL', status: 'ok', detail: 'v16.4 · 4 conexões ativas · Sem locks pendentes' },
        { name: 'Redis', status: 'ok', detail: 'v7.2.5 · Cache: 128 MB · Sem erros' },
        { name: 'SSL', status: 'ok', detail: 'Certificado válido · Expira em 87 dias · Let\'s Encrypt' },
        { name: 'Cloudflare', status: 'ok', detail: 'DNS ativo · CDN habilitado · WAF configurado' },
        { name: 'DNS', status: 'ok', detail: 'Registros A, CNAME, MX verificados · Propagação ok' },
        { name: 'Nginx', status: 'ok', detail: 'v1.26.1 · 2.3k req/min · Configuração válida' },
        { name: 'Firewall', status: 'ok', detail: 'UFW ativo · Portas 22, 80, 443, 8080 liberadas' },
      ];

      for (const result of results) {
        await new Promise((r) => setTimeout(r, 600));
        setItems((prev) => prev.map((item) =>
          item.name === result.name ? { ...item, status: result.status, detail: result.detail } : item
        ));
      }

      setRunning(false);
      setAllOk(results.every((r) => r.status === 'ok'));
      setHasErrors(results.some((r) => r.status === 'error'));
    };

    runDiagnostics();
  }, []);

  const handleRecheck = () => {
    setItems(initialDiagnostics);
    setRunning(true);
    setAllOk(false);
    setHasErrors(false);

    const runDiagnostics = async () => {
      const results: Array<{ name: string; status: 'ok' | 'error'; detail: string }> = [
        { name: 'Servidor', status: 'ok', detail: 'Ubuntu 24.04 LTS · Uptime 47 dias · Kernel 6.8.0' },
        { name: 'Docker', status: 'ok', detail: 'v26.1.3 · 12 containers em execução' },
        { name: 'Evolution API', status: 'ok', detail: 'v2.2.1 · 3 instâncias ativas · Conexão estável' },
        { name: 'PostgreSQL', status: 'ok', detail: 'v16.4 · 4 conexões ativas · Sem locks pendentes' },
        { name: 'Redis', status: 'ok', detail: 'v7.2.5 · Cache: 128 MB · Sem erros' },
        { name: 'SSL', status: 'ok', detail: 'Certificado válido · Expira em 87 dias · Let\'s Encrypt' },
        { name: 'Cloudflare', status: 'ok', detail: 'DNS ativo · CDN habilitado · WAF configurado' },
        { name: 'DNS', status: 'ok', detail: 'Registros A, CNAME, MX verificados · Propagação ok' },
        { name: 'Nginx', status: 'ok', detail: 'v1.26.1 · 2.3k req/min · Configuração válida' },
        { name: 'Firewall', status: 'ok', detail: 'UFW ativo · Portas 22, 80, 443, 8080 liberadas' },
      ];

      for (const result of results) {
        await new Promise((r) => setTimeout(r, 400));
        setItems((prev) => prev.map((item) =>
          item.name === result.name ? { ...item, status: result.status, detail: result.detail } : item
        ));
      }

      setRunning(false);
      setAllOk(results.every((r) => r.status === 'ok'));
    };

    runDiagnostics();
  };

  const okCount = items.filter((i) => i.status === 'ok').length;
  const errorCount = items.filter((i) => i.status === 'error').length;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">Diagnóstico do Sistema</h1>
        <p className="text-sm text-foreground-500">Verificação completa de todos os serviços e componentes da plataforma</p>
      </div>

      {/* Overall status */}
      {!running && (
        <div className={`rounded-2xl p-5 flex items-center gap-4 ${allOk ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${allOk ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            <i className={`text-2xl ${allOk ? 'ri-check-double-line text-emerald-600' : 'ri-error-warning-line text-amber-600'}`} />
          </div>
          <div>
            <p className={`text-base font-semibold ${allOk ? 'text-emerald-800' : 'text-amber-800'}`}>
              {allOk ? 'Todos os sistemas estão operacionais!' : `${errorCount} serviço(s) com problema`}
            </p>
            <p className={`text-sm ${allOk ? 'text-emerald-600' : 'text-amber-600'}`}>
              {allOk ? `${okCount}/${items.length} verificações concluídas com sucesso` : 'Execute uma nova verificação para confirmar'}
            </p>
          </div>
          <button onClick={handleRecheck} className="ml-auto px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-refresh-line" /> Verificar Novamente
          </button>
        </div>
      )}

      {running && (
        <div className="rounded-2xl p-5 bg-primary-50 border border-primary-200 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-600" />
          </div>
          <div>
            <p className="text-base font-semibold text-primary-800">Executando diagnóstico...</p>
            <p className="text-sm text-primary-600">Verificando todos os serviços da plataforma</p>
          </div>
        </div>
      )}

      {/* Diagnostics list */}
      <div className="bg-white rounded-2xl border border-background-200 overflow-hidden">
        <div className="divide-y divide-background-100">
          {items.map((item, i) => (
            <div key={item.name} className={`flex items-center gap-4 px-5 py-4 transition-all duration-300 ${i === items.filter((it) => it.status !== 'checking').length && running ? 'animate-pulse' : ''}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                item.status === 'ok' ? 'bg-emerald-50' : item.status === 'error' ? 'bg-red-50' : 'bg-background-100'
              }`}>
                <i className={`${item.icon} text-lg ${
                  item.status === 'ok' ? 'text-emerald-600' : item.status === 'error' ? 'text-red-500' : 'text-foreground-400'
                }`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                  {item.status === 'ok' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />}
                  {item.status === 'error' && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                  {item.status === 'checking' && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />}
                </div>
                <p className={`text-xs mt-0.5 ${
                  item.status === 'ok' ? 'text-emerald-600' : item.status === 'error' ? 'text-red-500' : 'text-foreground-500'
                }`}>{item.detail}</p>
              </div>
              <div className="flex-shrink-0">
                {item.status === 'ok' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold">
                    <i className="ri-check-line" /> OK
                  </span>
                )}
                {item.status === 'error' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-semibold">
                    <i className="ri-close-line" /> Erro
                  </span>
                )}
                {item.status === 'checking' && (
                  <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}